import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppLayout, PageBackLink } from '../../../components/layout';
import { OTRStatusBadge, OTRFilterBar, OTRPagination } from '../components';
import { getApprovalTravelRequestsPage } from '../../../services/outstationTravelService';
import { getCanonicalRoles } from '../../../lib/auth';
import { OTR_STATUS, getModeOfTravelName } from '../constants/otrEnums';

import { formatOtrDate } from '../utils/formatOtrDate';

// The approval report only contains requests/claims waiting for an approval step.
const REPORT_STATUSES = [
  OTR_STATUS.SUBMITTED,
  OTR_STATUS.L1_APPROVED,
  OTR_STATUS.EXPENSES_SUBMITTED,
  OTR_STATUS.EXPENSES_L1_APPROVED,
];


export const OTRReportPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 10;
  const [pageInfo, setPageInfo] = useState({ totalPages: 1, totalElements: 0 });

  const handleFilterChange = (next) => {
    setFilters(next);
    setPage(0);
  };

  // Finance users see the Finance report (all requests awaiting Finance);
  // everyone else sees the Team report (direct reports awaiting their approval).
  // The backend still decides what each caller may actually see.
  const isFinance = getCanonicalRoles().some((role) => role.includes('finance'));
  const reportType = isFinance ? 'finance' : 'team';

  useEffect(() => {
    let active = true;

    const loadRequests = async () => {
      setLoading(true);
      setError('');

      try {
        const data = await getApprovalTravelRequestsPage(reportType, { ...filters, page, size: PAGE_SIZE });
        if (active) {
          setRequests(data.content);
          setPageInfo({ totalPages: data.totalPages, totalElements: data.totalElements });
        }
      } catch (err) {
        console.error(`Failed to load ${reportType} outstation requests`, err);
        if (active) {
          setRequests([]);
          setPageInfo({ totalPages: 1, totalElements: 0 });
          setError(
            err?.response?.data?.message ||
            err?.message ||
            'Failed to load outstation report.'
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadRequests();
    return () => {
      active = false;
    };
  }, [filters, reportType, page]);

  return (
    <AppLayout>
      <PageBackLink to="/" />

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-[28px] font-bold text-foreground leading-tight">
            {isFinance ? 'Finance Report' : 'Team Report'}
          </h1>
          <p className="text-sm text-[var(--color-text-secondary)] mt-1">
            {isFinance ? 'Review travel requests awaiting Finance approval and take action.' : 'Review your team\'s travel requests and take approval actions.'}
          </p>
        </div>
      </div>

      <OTRFilterBar
        filters={filters}
        onFilterChange={handleFilterChange}
        statusOptions={REPORT_STATUSES}
      />

      {error && (
        <div className="mb-6 p-4 rounded-xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 text-sm text-[var(--color-error)]">
          {error}
        </div>
      )}

      {loading ? (
        <div className="py-20 text-center text-[var(--color-text-secondary)] font-medium italic">
          Loading requests...
        </div>
      ) : (
        <div className="bg-white rounded-2xl sm:rounded-[2rem] border border-[var(--color-border)] shadow-sm overflow-hidden">
          <table className="max-md:block w-full text-left border-collapse">
            <thead className="max-md:hidden">
              <tr className="bg-[var(--color-workspace)]/30 border-b border-[var(--color-border)]">
                <th className="px-6 py-4 text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">Reference</th>
                <th className="px-6 py-4 text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">Employee</th>
                <th className="px-6 py-4 text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">Destination</th>
                <th className="px-6 py-4 text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">Travel Dates</th>
                <th className="px-6 py-4 text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">{t('outstation.purpose')}</th>
                <th className="px-6 py-4 text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">Mode</th>
                <th className="px-6 py-4 text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="max-md:block divide-y divide-[var(--color-border)]">
              {requests.map((req) => (
                <tr
                  key={req.travelRequestId}
                  className="max-md:block max-md:px-4 max-md:py-3 hover:bg-[var(--color-workspace)]/10 transition-colors cursor-pointer"
                  onClick={() => navigate(`/outstation-travel/${req.travelRequestId}`)}
                >
                  <td data-label="Reference" className="max-md:flex max-md:items-center max-md:justify-between max-md:gap-3 max-md:px-0 max-md:py-1.5 max-md:text-right max-md:before:content-[attr(data-label)] max-md:before:text-[10px] max-md:before:font-bold max-md:before:uppercase max-md:before:tracking-wider max-md:before:text-left max-md:before:text-[var(--color-text-secondary)] max-md:before:shrink-0 px-6 py-5 text-sm font-bold text-[var(--color-primary)]">
                    {req.requestRef || `OTR-${req.travelRequestId}`}
                  </td>
                  <td data-label="Employee" className="max-md:flex max-md:items-center max-md:justify-between max-md:gap-3 max-md:px-0 max-md:py-1.5 max-md:text-right max-md:before:content-[attr(data-label)] max-md:before:text-[10px] max-md:before:font-bold max-md:before:uppercase max-md:before:tracking-wider max-md:before:text-left max-md:before:text-[var(--color-text-secondary)] max-md:before:shrink-0 px-6 py-5 text-sm font-medium text-foreground">
                    {req.employeeName || req.empName || req.empId || '—'}
                  </td>
                  <td data-label="Destination" className="max-md:flex max-md:items-center max-md:justify-between max-md:gap-3 max-md:px-0 max-md:py-1.5 max-md:text-right max-md:before:content-[attr(data-label)] max-md:before:text-[10px] max-md:before:font-bold max-md:before:uppercase max-md:before:tracking-wider max-md:before:text-left max-md:before:text-[var(--color-text-secondary)] max-md:before:shrink-0 px-6 py-5 text-sm font-medium text-foreground">
                    {req.placeOfTravel || '—'}
                  </td>
                  <td data-label="Travel Dates" className="max-md:flex max-md:items-center max-md:justify-between max-md:gap-3 max-md:px-0 max-md:py-1.5 max-md:text-right max-md:before:content-[attr(data-label)] max-md:before:text-[10px] max-md:before:font-bold max-md:before:uppercase max-md:before:tracking-wider max-md:before:text-left max-md:before:text-[var(--color-text-secondary)] max-md:before:shrink-0 px-6 py-5 text-sm text-[var(--color-text-secondary)]">
                    {formatOtrDate(req.dateOfTravel)} {t('outstation.dateRangeTo')} {formatOtrDate(req.dateOfReturn)}
                  </td>
                  <td data-label={t('outstation.purpose')} className="max-md:flex max-md:items-center max-md:justify-between max-md:gap-3 max-md:px-0 max-md:py-1.5 max-md:text-right max-md:before:content-[attr(data-label)] max-md:before:text-[10px] max-md:before:font-bold max-md:before:uppercase max-md:before:tracking-wider max-md:before:text-left max-md:before:text-[var(--color-text-secondary)] max-md:before:shrink-0 px-6 py-5 text-sm text-[var(--color-text-secondary)]">
                    {req.purpose || '—'}
                  </td>
                  <td data-label="Mode" className="max-md:flex max-md:items-center max-md:justify-between max-md:gap-3 max-md:px-0 max-md:py-1.5 max-md:text-right max-md:before:content-[attr(data-label)] max-md:before:text-[10px] max-md:before:font-bold max-md:before:uppercase max-md:before:tracking-wider max-md:before:text-left max-md:before:text-[var(--color-text-secondary)] max-md:before:shrink-0 px-6 py-5 text-sm text-[var(--color-text-secondary)]">
                    {getModeOfTravelName(req)}
                  </td>
                  <td data-label="Status" className="max-md:flex max-md:items-center max-md:justify-between max-md:gap-3 max-md:px-0 max-md:py-1.5 max-md:text-right max-md:before:content-[attr(data-label)] max-md:before:text-[10px] max-md:before:font-bold max-md:before:uppercase max-md:before:tracking-wider max-md:before:text-left max-md:before:text-[var(--color-text-secondary)] max-md:before:shrink-0 px-6 py-5">
                    <OTRStatusBadge status={req.status} />
                  </td>
                  <td data-label="Actions" className="max-md:flex max-md:items-center max-md:justify-between max-md:gap-3 max-md:px-0 max-md:py-1.5 max-md:text-right max-md:before:content-[attr(data-label)] max-md:before:text-[10px] max-md:before:font-bold max-md:before:uppercase max-md:before:tracking-wider max-md:before:text-left max-md:before:text-[var(--color-text-secondary)] max-md:before:shrink-0 px-6 py-5 text-right">
                    <button
                      type="button"
                      className="text-[var(--color-primary)] font-bold text-[12px] uppercase tracking-wider hover:underline"
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate(`/outstation-travel/${req.travelRequestId}`);
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {requests.length === 0 && !error && (
            <div className="py-20 text-center text-[var(--color-text-secondary)] italic">
              {filters.status
                ? 'No requests match the selected status.'
                : isFinance
                  ? 'No requests are awaiting Finance approval.'
                  : 'No team travel requests are awaiting your approval.'}
            </div>
          )}

          <OTRPagination
            page={page}
            totalPages={pageInfo.totalPages}
            totalElements={pageInfo.totalElements}
            onPageChange={setPage}
            disabled={loading}
          />
        </div>
      )}
    </AppLayout>
  );
};